/**
 * Export / Share control — shared by every report.
 *
 * Both actions are server-side (`GET /reports/export`, `POST /reports/share`).
 * The browser holds only what is on screen and none of the number formatting,
 * so a client-built file would quietly disagree with the report it came from —
 * and Excel/PDF cannot be produced here without shipping those libraries to
 * every user.
 *
 * The current filter set and the user's visible columns are both forwarded, so
 * the file matches exactly what the user is looking at, including a column
 * order they arranged themselves.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import {
  Download, FileSpreadsheet, FileText, FileType, Share2, ChevronDown, Mail,
  MessageCircle, Loader2, X,
} from 'lucide-react'

import { Dropdown } from 'primereact/dropdown'

import { api } from '../../api/client.js'
import { useToast } from '../system/ToastProvider.js'
import RecipientsChipsInput from '../admin/reports/RecipientsChipsInput.js'

interface CountryOption { id: number; name: string; iso2: string | null; phonecode: string | null }

/** Dial code shown until the country list arrives — India, the default. */
const DEFAULT_DIAL = '91'

type Format = 'csv' | 'xlsx' | 'pdf'

interface ReportExportMenuProps {
  reportId: string
  reportName: string
  /**
   * The filter set as flat query params, or null until the report has been
   * run. A record rather than one report's filter type, so every report can
   * share this menu — each already knows how to serialise its own filters.
   */
  params: Record<string, string> | null
  /** Visible column keys, in the user's order. */
  columns: string[]
  disabled: boolean
  controlClass: string
  menuClass: string
}

const FORMATS: { value: Format; label: string; icon: typeof FileText }[] = [
  { value: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
  { value: 'csv', label: 'CSV (.csv)', icon: FileType },
  { value: 'pdf', label: 'PDF (.pdf)', icon: FileText },
]

/** Report id + the caller's filter params + the visible columns. */
function toParams(
  reportId: string,
  params: Record<string, string>,
  columns: string[],
): URLSearchParams {
  const p = new URLSearchParams({ report: reportId, columns: columns.join(',') })
  for (const [k, v] of Object.entries(params)) {
    if (v !== '') p.set(k, v)
  }
  return p
}

export default function ReportExportMenu({
  reportId,
  reportName,
  params,
  columns,
  disabled,
  controlClass,
  menuClass,
}: ReportExportMenuProps): JSX.Element {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<Format | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const download = async (format: Format): Promise<void> => {
    if (!params) return
    setBusy(format)
    setOpen(false)
    try {
      const qs = toParams(reportId, params, columns)
      qs.set('format', format)
      // Not api.get(): this returns a binary body, and the shared client
      // parses JSON. The auth cookie rides along on a same-origin fetch.
      const res = await fetch(`/api/reports/export?${qs.toString()}`, {
        credentials: 'include',
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => null) as { message?: string } | null
        throw new Error(detail?.message ?? `Export failed (${res.status})`)
      }
      const blob = await res.blob()
      const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
        ?? `${reportId}.${format}`
      // The anchor must be IN the document (Firefox ignores a detached one),
      // and the object URL must outlive the click — revoking it synchronously
      // cancels the download before the browser has read the blob, which is
      // why the request succeeded (200) but no file ever appeared.
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.rel = 'noopener'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 1000)
    } catch (err) {
      toast.error('Export failed', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={`${controlClass} disabled:cursor-not-allowed disabled:opacity-50`}
        title={disabled ? 'Run the report first' : 'Export or share'}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-[#3366FF]" />
        ) : (
          <Download className="h-4 w-4 text-slate-400" />
        )}
        Export
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`${menuClass} right-0 w-56`}>
          <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
            Download
          </p>
          {FORMATS.map((f) => (
            <button
              key={f.value}
              onClick={() => { void download(f.value) }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <f.icon className="h-4 w-4 text-slate-400" />
              {f.label}
            </button>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            onClick={() => { setOpen(false); setShareOpen(true) }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Share2 className="h-4 w-4 text-slate-400" />
            Share…
          </button>
        </div>
      )}

      {shareOpen && params && (
        <ShareDialog
          reportId={reportId}
          reportName={reportName}
          params={params}
          columns={columns}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  )
}

/** Email sends the file as an attachment; WhatsApp opens a pre-filled draft. */
function ShareDialog({
  reportId,
  reportName,
  params,
  columns,
  onClose,
}: {
  reportId: string
  reportName: string
  params: Record<string, string>
  columns: string[]
  onClose: () => void
}): JSX.Element {
  const toast = useToast()
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email')
  // Emails are chips (one validated address each); the WhatsApp number is a
  // single free-text field paired with a country dial code.
  const [emails, setEmails] = useState<string[]>([])
  const [phone, setPhone] = useState('')
  const [dial, setDial] = useState(DEFAULT_DIAL)
  const [countries, setCountries] = useState<CountryOption[]>([])
  const [format, setFormat] = useState<Format>('xlsx')
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)

  // Same source every other country picker in the product uses, so the list
  // and its dial codes cannot drift from the rest of the app.
  useEffect(() => {
    let cancelled = false
    void api.get<{ countries: CountryOption[] }>('/geo/countries/dropdown')
      .then((res) => { if (!cancelled) setCountries(res.countries ?? []) })
      .catch(() => { /* the default dial code still works */ })
    return () => { cancelled = true }
  }, [])

  const dialOptions = countries
    .filter((c) => c.phonecode)
    .map((c) => ({ label: `${c.iso2 ?? ''} +${c.phonecode}`.trim(), value: String(c.phonecode) }))

  const summary = `${reportName} · ${params['startDate'] ?? ''} → ${params['endDate'] ?? ''} (${params['timezone'] ?? 'UTC'})`
  const canSend = channel === 'email' ? emails.length > 0 : phone.trim().length > 0

  const send = async (): Promise<void> => {
    if (!canSend) return
    if (channel === 'whatsapp') {
      // The API cannot attach a file to WhatsApp without a hosted media URL,
      // so rather than pretend, this opens a draft with the summary and lets
      // the user attach the downloaded file themselves.
      // wa.me wants country code + number with no punctuation.
      const digits = `${dial}${phone}`.replace(/[^\d]/g, '')
      const text = encodeURIComponent(`${note ? `${note}\n\n` : ''}${summary}`)
      window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener')
      onClose()
      return
    }

    setSending(true)
    try {
      await api.post('/reports/share', {
        ...params,
        report: reportId,
        format,
        channel,
        to: emails.join(','),
        message: note,
        columns: columns.join(','),
      })
      toast.success('Report sent')
      onClose()
    } catch (err) {
      toast.error('Could not share', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Share report"
        className="fixed left-1/2 top-1/2 z-[9999] w-[420px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-[3px] bg-white shadow-2xl ring-1 ring-black/5"
      >
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
              <Share2 className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-bold leading-tight text-[#0f172a]">Share report</h2>
              <p className="truncate text-[11.5px] leading-tight text-slate-400">{summary}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <div className="flex gap-1 rounded-[3px] bg-slate-100 p-1">
            {(['email', 'whatsapp'] as const).map((c) => (
              <button
                key={c}
                onClick={() => setChannel(c)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-[3px] px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                  channel === c
                    ? 'bg-white text-[#3366FF] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {c === 'email' ? <Mail className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                {c === 'email' ? 'Email' : 'WhatsApp'}
              </button>
            ))}
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
              {channel === 'email' ? 'Recipients' : 'Phone number'}
            </label>

            {channel === 'email' ? (
              <>
                {/* Same chips input the scheduled-report drawer uses — it
                    rejects anything that is not a valid address on add, so an
                    invalid recipient never reaches the request. */}
                <RecipientsChipsInput value={emails} onChange={setEmails} />
                <p className="mt-1 text-[11px] text-slate-400">
                  Press Enter after each address · up to 10.
                </p>
              </>
            ) : (
              <div className="flex gap-2">
                <Dropdown
                  value={dial}
                  options={dialOptions}
                  onChange={(e) => setDial(String(e.value))}
                  filter
                  placeholder="Code"
                  className="w-[8.5rem] shrink-0"
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="9876543210"
                  className="w-full rounded-[3px] border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm outline-none transition-colors focus:border-[#3366FF] focus:bg-white"
                />
              </div>
            )}
          </div>

          {channel === 'email' && (
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">Attach as</label>
              <div className="flex gap-2">
                {FORMATS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFormat(f.value)}
                    className={`flex-1 rounded-[3px] border px-2 py-1.5 text-[12px] font-semibold transition-colors ${
                      format === f.value
                        ? 'border-[#C9D9FB] bg-blue-50 text-[#3366FF]'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {f.value.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">Message (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-[3px] border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm outline-none transition-colors focus:border-[#3366FF] focus:bg-white"
            />
          </div>

          {channel === 'whatsapp' && (
            <p className="rounded-[3px] bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700">
              WhatsApp opens a draft with the report summary — attach the downloaded
              file yourself. Sending the file directly needs hosted media, which is
              not set up yet.
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3.5">
          <button
            onClick={onClose}
            className="rounded-[3px] border border-slate-200 px-4 py-2 text-[12.5px] font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { void send() }}
            disabled={sending || !canSend}
            className="inline-flex items-center gap-2 rounded-[3px] bg-[#3366FF] px-5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {channel === 'email' ? 'Send email' : 'Open WhatsApp'}
          </button>
        </footer>
      </div>
    </>
  )
}
